/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { Button } from "../../ui/button";
import CustomDataGrid from "../../Shared/Table/CustomDataGrid";
import { siteMigrationColumn } from "../../Shared/Table/Columns";
import { ILoginRequest, IMigration } from "../../../store/api/migration/types";
import { useStoreDispatch } from "../../../store/hooks";
import { setDeviceLoginRequest, setIsConnected } from "../../../store/api/migration/reducers";
import { useSelector } from "react-redux";
import { loginRequestSelector } from "../../../store/api/migration/selectors";
import { initialState } from "../../../store/api/migration/initialState";
import { GridRowParams, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { noOfSelectedRowSelector } from "../../../store/api/devices/selectors";
import { setNoOfSelectedRow } from "../../../store/api/devices/reducers";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import SiteMigrationFilter from "./Filters/SiteMigrationFilter";

interface ISiteMigrationProps {
  siteMigrationData: IMigration[];
  setSiteMigrationData: (value: React.SetStateAction<IMigration[]>) => void;
  loginStatus: any;
  setSelectedRow: (value: React.SetStateAction<IMigration[]>) => void;
}

const SiteMigration: React.FC<ISiteMigrationProps> = ({ siteMigrationData, loginStatus, setSelectedRow, setSiteMigrationData }) => {
  const dispatch = useStoreDispatch();
  const loginRequest: ILoginRequest = useSelector(loginRequestSelector);
  const { siteMigrationRowIds = [] } = useSelector(noOfSelectedRowSelector);

  const siteCloseHandler = () => {
    dispatch(setIsConnected(false));
    dispatch(setDeviceLoginRequest(initialState.loginRequest));
  };

  const handleOnRowSelected = (rowIds: GridRowSelectionModel) => {
    dispatch(setNoOfSelectedRow({ siteMigrationRowIds: rowIds }));
    setSelectedRow(() => siteMigrationData.filter((item) => rowIds.includes(item.serialNumber)));
  };

  const handleOnClickMigrate = () => {
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.MIGRATESITETOREGION));
  };

  const handleOnClear = () => {
    dispatch(setNoOfSelectedRow({ siteMigrationRowIds: [] }));
  };

  return (
    <div className="flex flex-col gap-7">
      <div className="flex justify-between items-center">
        <p className="text-base font-medium text-foreground">SIte Migration</p>
        <Button variant="outline" size="sm" onClick={siteCloseHandler}>
          Close
        </Button>
      </div>
      <div className="border-b border-border" />
      <div className="flex items-center bg-muted pl-10">
        <div className="w-1/2">
          <p className="text-sm text-foreground">URL: {loginRequest.hostAddress}</p>
        </div>
        <div className="w-1/2">
          <p className="text-sm text-foreground">{`DEVICES: ${siteMigrationData?.length}`}</p>
        </div>
      </div>
      <div>
        <SiteMigrationFilter setSiteMigrationData={setSiteMigrationData} />
      </div>
      {siteMigrationRowIds.length > 0 && (
        <div className="flex items-center">
          <p className="text-sm font-medium text-foreground">{`${siteMigrationRowIds.length} Selected`}</p>
          <Button variant="default" className="mx-8" onClick={handleOnClickMigrate}>
            Migrate
          </Button>
          <Button variant="ghost" onClick={handleOnClear}>Clear</Button>
        </div>
      )}
      <div className="w-full">
        <CustomDataGrid
          tableId="siteMigration"
          rows={siteMigrationData}
          columns={siteMigrationColumn}
          rowCount={siteMigrationData.length}
          loading={loginStatus.isLoading}
          checkboxSelection={true}
          rowSelectionModel={siteMigrationRowIds}
          disableRowSelectionOnClick={true}
          onRowSelectionModelChange={handleOnRowSelected}
          getRowId={(row: IMigration) => row.serialNumber}
          sortingMode="client"
          paginationMode="client"
          isRowSelectable={(grid: GridRowParams<any>) => grid.row.migrationStatus !== "Complete"}
        />
      </div>
    </div>
  );
};

export default SiteMigration;
